/**
 * @fileoverview Department mapping editor for boundary and map-default configuration.
 * @module system-web/pages/DepartmentMappingTab
 *
 * Responsibilities:
 * - Present and update department boundary metadata.
 * - Edit map defaults and preview saved/current map camera settings.
 * Dependencies: React, shared admin UI, DepartmentMapView, and BoundarySearchPanel.
 * Architecture: Receives local-first tenant data and writes through the caller-provided save callback.
 * Notes: Camera preview props must remain referentially stable across local-only state updates.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Map, Target, HelpCircle } from 'lucide-react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { InlineEditableField } from '@ui-admin/components/InlineEditableField';
import type { InlineEditFieldConfig } from '@ui-admin/components/InlineEditableField';
import DepartmentMapView from '../components/DepartmentMapView';
import BoundarySearchPanel from '../components/BoundarySearchPanel';
import type { BoundaryGeometry } from '../utils/boundaryUtils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cfg(fieldKey: string, editType: InlineEditFieldConfig['editType'], rest?: Partial<InlineEditFieldConfig>): InlineEditFieldConfig {
  return { fieldKey, editType, ...rest };
}

function PanelSection({ title, icon: Icon, children, expandedBg, textSecondary }: {
  title: string;
  icon: any;
  children: React.ReactNode;
  expandedBg?: string;
  textSecondary?: string;
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5" style={{ color: textSecondary }} />
        <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>{title}</h4>
      </div>
      <div className="rounded-lg p-3" style={{ background: expandedBg }}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Map priority cascade tooltip
// ---------------------------------------------------------------------------
function MapPriorityCascade({ theme }: { theme: any }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs"
        style={{ color: theme.textSecondary }}
      >
        <HelpCircle className="h-3.5 w-3.5" />
        <span>Map centering priority</span>
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-20 w-72 rounded-lg shadow-lg p-3 text-xs space-y-1.5"
          style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.textSecondary }}
        >
          <p className="font-semibold mb-1" style={{ color: theme.textPrimary }}>Map centering priority (highest → lowest)</p>
          <p><span className="font-medium" style={{ color: theme.textPrimary }}>1. User location</span> — if the browser can read GPS, the map centers on the user</p>
          <p><span className="font-medium" style={{ color: theme.textPrimary }}>2. Department boundary</span> — if a boundary is set, the map fits to it</p>
          <p><span className="font-medium" style={{ color: theme.textPrimary }}>3. Map defaults below</span> — the absolute fallback when neither user location nor boundary is available</p>
          <button type="button" onClick={() => setOpen(false)} className="mt-1 text-xs underline" style={{ color: theme.accent }}>Close</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DepartmentMappingTab
// ---------------------------------------------------------------------------

/**
 * @contract DepartmentMappingTabProps
 * Purpose: Supplies the department snapshot and its local-first field mutation callback.
 * Fields: tenant, onSave.
 * Rules: onSave persists one mapping field and resolves after the local write is accepted.
 * Sync: Parent owns server synchronization; this component renders the provided tenant snapshot.
 */
export interface DepartmentMappingTabProps {
  tenant: any;
  onSave: (fieldKey: string, value: unknown) => Promise<void>;
}

/**
 * @component DepartmentMappingTab
 * Purpose: Edit a department boundary and its default map camera/style settings.
 * Props: DepartmentMappingTabProps.
 * Behavior: Boundary actions use onSave; preview camera changes remain local until explicitly saved.
 * Data Source: Hybrid — tenant data is supplied by the parent and writes use its local-first callback.
 * Constraints: No direct API access; camera coordinates are memoized to avoid feedback loops with Mapbox events.
 * Performance: Keep preview center identity stable unless longitude or latitude actually changes.
 */
export default function DepartmentMappingTab({ tenant, onSave }: DepartmentMappingTabProps) {
  const { theme } = useTheme();

  const boundary: BoundaryGeometry | null = tenant.boundary ?? null;
  const boundaryCentroid: [number, number] | null = tenant.boundaryCentroid ?? null;
  const boundaryBbox: [number, number, number, number] | null = tenant.boundaryBbox ?? null;
  const boundaryArea: number | null = tenant.boundaryArea ?? null;
  const mapDef = tenant.mapDefaults ?? {};

  // Local preview state for the map defaults live preview
  const [previewCenter, setPreviewCenter] = useState<[number, number] | null>(
    mapDef.centerLongitude != null && mapDef.centerLatitude != null
      ? [mapDef.centerLongitude, mapDef.centerLatitude]
      : null,
  );
  const [previewZoom, setPreviewZoom] = useState<number | null>(mapDef.defaultZoom ?? null);

  // Keep preview state in sync when the tenant prop is replaced from outside
  useEffect(() => {
    const md = tenant.mapDefaults ?? {};
    setPreviewCenter(
      md.centerLongitude != null && md.centerLatitude != null
        ? [md.centerLongitude, md.centerLatitude]
        : null,
    );
    setPreviewZoom(md.defaultZoom ?? null);
  }, [tenant]);

  // ---------------------------------------------------------------------------
  // Boundary handlers
  // ---------------------------------------------------------------------------
  const handleBoundarySelect = useCallback(async (
    geom: BoundaryGeometry,
    nerisId: string,
    centroid: [number, number],
    bbox: [number, number, number, number],
    area: number,
  ) => {
    try {
      await onSave('boundary', geom);
      await onSave('boundaryCentroid', centroid);
      await onSave('boundaryBbox', bbox);
      await onSave('boundaryArea', area);
      await onSave('nerisId', nerisId);
    } catch (err) {
      console.error('[DepartmentMappingTab] handleBoundarySelect failed:', err);
    }
  }, [onSave]);

  const handleBoundaryClear = useCallback(async () => {
    try {
      await onSave('boundary', null);
      await onSave('boundaryCentroid', null);
      await onSave('boundaryBbox', null);
      await onSave('boundaryArea', null);
      await onSave('nerisId', null);
    } catch (err) {
      console.error('[DepartmentMappingTab] handleBoundaryClear failed:', err);
    }
  }, [onSave]);

  // ---------------------------------------------------------------------------
  // "Set from boundary center" button
  // ---------------------------------------------------------------------------
  const handleSetFromBoundaryCenter = useCallback(async () => {
    if (!boundaryCentroid) return;
    try {
      await onSave('mapDefaults.centerLongitude', boundaryCentroid[0]);
      await onSave('mapDefaults.centerLatitude', boundaryCentroid[1]);
      setPreviewCenter(boundaryCentroid);
    } catch (err) {
      console.error('[DepartmentMappingTab] handleSetFromBoundaryCenter failed:', err);
    }
  }, [boundaryCentroid, onSave]);

  // ---------------------------------------------------------------------------
  // "Set from current view" — captures map center/zoom into fields
  // ---------------------------------------------------------------------------
  const handleMapCenterChange = useCallback((center: [number, number]) => {
    setPreviewCenter(center);
  }, []);

  const handleMapZoomChange = useCallback((zoom: number) => {
    setPreviewZoom(zoom);
  }, []);

  const handleSetFromCurrentView = useCallback(async () => {
    try {
      if (previewCenter) {
        await onSave('mapDefaults.centerLongitude', parseFloat(previewCenter[0].toFixed(6)));
        await onSave('mapDefaults.centerLatitude', parseFloat(previewCenter[1].toFixed(6)));
      }
      if (previewZoom != null) {
        await onSave('mapDefaults.defaultZoom', Math.round(previewZoom));
      }
    } catch (err) {
      console.error('[DepartmentMappingTab] handleSetFromCurrentView failed:', err);
    }
  }, [previewCenter, previewZoom, onSave]);

  // ---------------------------------------------------------------------------
  // Derived map center for the defaults preview
  // ---------------------------------------------------------------------------
  const defaultsMapCenter = useMemo<[number, number] | null>(
    () => mapDef.centerLongitude != null && mapDef.centerLatitude != null
      ? [mapDef.centerLongitude, mapDef.centerLatitude]
      : null,
    [mapDef.centerLongitude, mapDef.centerLatitude],
  );

  return (
    <div className="space-y-1">

      {/* ── Section 1: Department Boundary ── */}
      <PanelSection title="Department Boundary" icon={Map} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
        <div className="space-y-3">
          <BoundarySearchPanel
            tenantName={tenant.name}
            currentNerisId={tenant.nerisId}
            hasBoundary={!!boundary}
            onSelect={handleBoundarySelect}
            onClear={handleBoundaryClear}
            theme={theme}
          />

          {/* Live map preview for boundary */}
          <DepartmentMapView
            boundary={boundary}
            center={boundaryCentroid}
            className="w-full h-52"
          />

          {/* Boundary metadata */}
          {boundary && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs pt-1">
              {boundaryCentroid && (
                <>
                  <div>
                    <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>Centroid (lng)</dt>
                    <dd className="font-mono" style={{ color: theme.textPrimary }}>{boundaryCentroid[0].toFixed(6)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>Centroid (lat)</dt>
                    <dd className="font-mono" style={{ color: theme.textPrimary }}>{boundaryCentroid[1].toFixed(6)}</dd>
                  </div>
                </>
              )}
              {boundaryBbox && (
                <div className="col-span-2">
                  <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>Bounding box</dt>
                  <dd className="font-mono text-xs break-all" style={{ color: theme.textPrimary }}>
                    [{boundaryBbox.map((v) => v.toFixed(4)).join(', ')}]
                  </dd>
                </div>
              )}
              {boundaryArea != null && (
                <div>
                  <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>Area (approx.)</dt>
                  <dd style={{ color: theme.textPrimary }}>{boundaryArea.toFixed(1)} sq mi</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </PanelSection>

      {/* ── Section 2: Map Defaults ── */}
      <PanelSection title="Map Defaults" icon={Target} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
        <div className="space-y-3">

          <MapPriorityCascade theme={theme} />

          {/* Quick-fill from boundary center */}
          {boundaryCentroid && (
            <button
              type="button"
              onClick={handleSetFromBoundaryCenter}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors"
              style={{ background: theme.expandedCardBorder, color: theme.textPrimary }}
            >
              <Map className="h-3.5 w-3.5" style={{ color: theme.accent }} />
              Set center from boundary centroid
            </button>
          )}

          {/* Editable defaults */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <InlineEditableField
              label="Default Zoom"
              value={mapDef.defaultZoom != null ? String(mapDef.defaultZoom) : '—'}
              editValue={mapDef.defaultZoom ?? 10}
              config={cfg('mapDefaults.defaultZoom', 'number', { numberConfig: { min: 1, max: 22, step: 1 } })}
              onSave={onSave}
            />
            <InlineEditableField
              label="Map Style"
              value={mapDef.mapStyle || '—'}
              config={cfg('mapDefaults.mapStyle', 'select', {
                options: [
                  { value: 'streets',   label: 'Streets'   },
                  { value: 'satellite', label: 'Satellite' },
                  { value: 'hybrid',    label: 'Hybrid'    },
                ],
              })}
              onSave={onSave}
            />
            <InlineEditableField
              label="Center Latitude"
              value={mapDef.centerLatitude != null ? String(mapDef.centerLatitude) : '—'}
              editValue={mapDef.centerLatitude ?? 0}
              config={cfg('mapDefaults.centerLatitude', 'number', { numberConfig: { min: -90, max: 90, step: 0.0001 } })}
              onSave={onSave}
            />
            <InlineEditableField
              label="Center Longitude"
              value={mapDef.centerLongitude != null ? String(mapDef.centerLongitude) : '—'}
              editValue={mapDef.centerLongitude ?? 0}
              config={cfg('mapDefaults.centerLongitude', 'number', { numberConfig: { min: -180, max: 180, step: 0.0001 } })}
              onSave={onSave}
            />
            <InlineEditableField
              label="Traffic Layer"
              value={mapDef.trafficLayer != null ? (mapDef.trafficLayer ? 'Yes' : 'No') : '—'}
              editValue={mapDef.trafficLayer ?? false}
              config={cfg('mapDefaults.trafficLayer', 'boolean')}
              onSave={onSave}
            />
            <InlineEditableField
              label="Measurement Unit"
              value={mapDef.measurementUnit || '—'}
              config={cfg('mapDefaults.measurementUnit', 'select', {
                options: [
                  { value: 'imperial', label: 'Imperial' },
                  { value: 'metric',   label: 'Metric'   },
                ],
              })}
              onSave={onSave}
            />
            <InlineEditableField
              label="Cluster Markers"
              value={mapDef.clusterMarkers != null ? (mapDef.clusterMarkers ? 'Yes' : 'No') : '—'}
              editValue={mapDef.clusterMarkers ?? true}
              config={cfg('mapDefaults.clusterMarkers', 'boolean')}
              onSave={onSave}
            />
          </dl>

          {/* Live preview for map defaults */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>Live preview</span>
              <button
                type="button"
                onClick={handleSetFromCurrentView}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded font-medium transition-colors"
                style={{ color: theme.accent }}
              >
                <Target className="h-3 w-3" />
                Set from current view
              </button>
            </div>
            <DepartmentMapView
              center={defaultsMapCenter}
              zoom={mapDef.defaultZoom ?? null}
              mapStyle={mapDef.mapStyle ?? null}
              boundary={null}
              onCenterChange={handleMapCenterChange}
              onZoomChange={handleMapZoomChange}
              className="w-full h-48"
            />
            {(previewCenter || previewZoom != null) && (
              <p className="text-xs" style={{ color: theme.textMuted }}>
                Current view: {previewCenter ? `${previewCenter[1].toFixed(4)}°N, ${previewCenter[0].toFixed(4)}°E` : ''}
                {previewZoom != null ? ` · zoom ${previewZoom.toFixed(1)}` : ''}
              </p>
            )}
          </div>
        </div>
      </PanelSection>

    </div>
  );
}
